import Config from './config'
import type { BaseEvent } from '../types/events'

interface QueueItem {
  event: BaseEvent
  retries: number
  timestamp: number
}

export class EventQueue {
  private static instance: EventQueue
  private queue: QueueItem[] = []
  private flushTimer: number | null = null
  private isFlushing: boolean = false
  private maxRetries: number = 3

  private constructor() {
    this.startFlushTimer()
    this.setupBeforeUnload()
  }

  public static getInstance(): EventQueue {
    if (!EventQueue.instance) {
      EventQueue.instance = new EventQueue()
    }
    return EventQueue.instance
  }

  /**
   * Add event to queue
   */
  public enqueue(event: BaseEvent): void {
    this.queue.push({
      event,
      retries: 0,
      timestamp: Date.now()
    })

    if (Config.isDebug()) {
      console.log('[RadarSnap] Event queued:', event.event_type, event)
    }

    // Check if we need to flush immediately
    if (this.queue.length >= Config.get('batchSize')) {
      this.flush()
    }
  }

  /**
   * Start automatic flush timer
   */
  private startFlushTimer(): void {
    const interval = Config.get('flushInterval')
    
    this.flushTimer = window.setInterval(() => {
      if (this.queue.length > 0) {
        this.flush()
      }
    }, interval)
  }

  /**
   * Flush queue - send all events to backend
   */
  public async flush(): Promise<void> {
    if (this.isFlushing || this.queue.length === 0) {
      return
    }

    this.isFlushing = true

    // Take all events from queue
    const itemsToSend = [...this.queue]
    this.queue = []

    try {
      const events = itemsToSend.map(item => item.event)
      await this.sendEvents(events)

      if (Config.isDebug()) {
        console.log(`[RadarSnap] Successfully sent ${events.length} events`)
      }
    } catch (error) {
      console.error('[RadarSnap] Failed to send events:', error)
      
      // Re-queue failed events with retry logic
      itemsToSend.forEach(item => {
        if (item.retries < this.maxRetries) {
          this.queue.push({
            ...item,
            retries: item.retries + 1
          })
        } else {
          if (Config.isDebug()) {
            console.warn('[RadarSnap] Event dropped after max retries:', item.event)
          }
        }
      })
    } finally {
      this.isFlushing = false
    }
  }

  /**
   * Send events to backend API
   */
  private async sendEvents(events: BaseEvent[]): Promise<void> {
    const apiKey = Config.get('apiKey')
    const endpoint = Config.get('endpoint')

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey
      },
      body: JSON.stringify({ events }),
      keepalive: true // Important: ensures request completes even if page unloads
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Setup beforeunload handler to flush events before page closes
   */
  private setupBeforeUnload(): void {
    window.addEventListener('beforeunload', () => {
      if (this.queue.length > 0) {
        // Use sendBeacon for guaranteed delivery during page unload
        this.sendBeacon()
      }
    })

    // Also flush on visibility change (when tab becomes hidden)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && this.queue.length > 0) {
        this.sendBeacon()
      }
    })
  }

  /**
   * Send events using sendBeacon API (guaranteed delivery on page unload)
   */
  private sendBeacon(): void {
    if (this.queue.length === 0) return

    const apiKey = Config.get('apiKey')
    const endpoint = Config.get('endpoint')
    const events = this.queue.map(item => item.event)

    const blob = new Blob(
      [JSON.stringify({ events })],
      { type: 'application/json' }
    )

    const headers = {
      'X-API-Key': apiKey
    }

    // Note: sendBeacon doesn't support custom headers, so append API key to URL
    const beaconUrl = `${endpoint}?api_key=${apiKey}`

    const sent = navigator.sendBeacon(beaconUrl, blob)

    if (Config.isDebug()) {
      console.log(`[RadarSnap] sendBeacon ${sent ? 'succeeded' : 'failed'} for ${events.length} events`)
    }

    // Clear queue after attempt
    this.queue = []
  }

  /**
   * Clear queue and stop timers
   */
  public destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer)
      this.flushTimer = null
    }
    this.queue = []
  }

  /**
   * Get current queue size
   */
  public size(): number {
    return this.queue.length
  }
}

export default EventQueue.getInstance()